import { Component, type ErrorInfo, type ReactNode, useCallback, useState } from "react";
import { captureException, captureMessage, trackEvent, setUser, setContext, flush } from "../autter";
import { Bug, Lightning, PaperPlaneTilt, X } from "@phosphor-icons/react";

class TestErrorBoundary extends Component<
  { children: ReactNode; onCaught: () => void },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureException(error, {
      react: true,
      componentStack: String(info.componentStack).slice(0, 4_000),
      source: "test_panel_render_error",
    });
    this.props.onCaught();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-md border-2 border-signal bg-signal/10 px-4 py-3 text-sm font-semibold text-signal-deep">
          Error caught by boundary and sent to Autter.
        </div>
      );
    }
    return this.props.children;
  }
}

function SectionCard({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-line bg-foam p-5 shadow-card">
      <h3 className="mb-4 flex items-center gap-2 font-mono text-xs font-semibold tracking-[0.1em] text-muted uppercase">
        {icon}
        {title}
      </h3>
      <div className="flex flex-wrap gap-3">{children}</div>
    </div>
  );
}

function TestButton({
  label,
  onClick,
  variant = "ghost",
}: {
  label: string;
  onClick: () => void;
  variant?: "ghost" | "signal";
}) {
  const base =
    "inline-flex min-h-10 items-center gap-2 rounded-full px-5 text-sm font-semibold transition-colors";
  const cls =
    variant === "signal"
      ? `${base} bg-signal text-ink hover:bg-signal-deep hover:text-foam`
      : `${base} border-2 border-ink text-ink hover:bg-ink hover:text-foam`;
  return (
    <button type="button" onClick={onClick} className={cls}>
      {label}
    </button>
  );
}

export function AutterTestPanel() {
  const [boundaryKey, setBoundaryKey] = useState(0);
  const [lastStatus, setLastStatus] = useState<string | null>(null);

  const handleThrowRender = useCallback(() => {
    setBoundaryKey((k) => k + 1);
  }, []);

  const handleThrowJs = useCallback(() => {
    throw new Error("Intentional JS error from AutterTestPanel button click");
  }, []);

  const handlePromiseRejection = useCallback(() => {
    Promise.reject(new Error("Unhandled promise rejection from AutterTestPanel"));
    setLastStatus("Promise rejected — check unhandledrejection capture.");
  }, []);

  const handleConsoleError = useCallback(() => {
    console.error("[AutterTestPanel] Console error test —", new Date().toISOString());
    setLastStatus("console.error fired.");
  }, []);

  const handleMessage = useCallback(() => {
    captureMessage("Test message from AutterTestPanel");
    setLastStatus("captureMessage sent.");
  }, []);

  const handleTrackEvent = useCallback(() => {
    trackEvent("test_button_clicked", { source: "test_panel", timestamp: Date.now() });
    setLastStatus("trackEvent sent.");
  }, []);

  const handleSetUser = useCallback(() => {
    setUser("test-user-123");
    setContext({ panel: "test_panel", started_at: new Date().toISOString(), role: "tester" });
    captureMessage("User context set — id: test-user-123");
    setLastStatus("setUser + setContext applied, message sent with context.");
  }, []);

  const handleFlush = useCallback(() => {
    flush();
    setLastStatus("flush() called — queued events drained.");
  }, []);

  return (
    <main className="chart-grid min-h-[100dvh] bg-paper px-5 py-20">
      <div className="mx-auto max-w-2xl">
        <div className="mb-10 flex items-center justify-between">
          <div>
            <h1 className="display-type font-black text-[clamp(2rem,5vw,3.5rem)] text-ink">
              Autter Runtime Test
            </h1>
            <p className="mt-2 font-mono text-xs text-muted">
              Navigate to <code className="rounded bg-paper-deep px-1.5 py-0.5">{"#test"}</code> to see
              this panel. Works in prod / preview only.
            </p>
          </div>
          <a
            href="#"
            className="inline-flex size-10 items-center justify-center rounded-full border border-line text-muted transition-colors hover:bg-paper-deep hover:text-ink"
            aria-label="Close test panel"
          >
            <X className="size-5" weight="bold" />
          </a>
        </div>

        <div className="space-y-6">
          <SectionCard title="Error Testing" icon={<Bug className="size-4" weight="bold" />}>
            <TestErrorBoundary key={boundaryKey} onCaught={() => setLastStatus("Render error caught by boundary.")}>
              <TestButton label="Throw Render Error" onClick={handleThrowRender} variant="signal" />
            </TestErrorBoundary>
            <TestButton label="Throw JS Error" onClick={handleThrowJs} variant="signal" />
            <TestButton label="Unhandled Promise Rejection" onClick={handlePromiseRejection} variant="signal" />
            <TestButton label="Console Error" onClick={handleConsoleError} variant="signal" />
          </SectionCard>

          <SectionCard title="Runtime Verification" icon={<PaperPlaneTilt className="size-4" weight="bold" />}>
            <TestButton label="Send Test Message" onClick={handleMessage} />
            <TestButton label="Track Custom Event" onClick={handleTrackEvent} />
            <TestButton label="Set User + Context" onClick={handleSetUser} />
            <TestButton label="Flush Events" onClick={handleFlush} />
          </SectionCard>

          {lastStatus && (
            <div className="flex items-center gap-2 rounded-md border border-sea-soft bg-sea-soft/30 px-4 py-3 font-mono text-xs text-ink-soft">
              <Lightning className="size-4 shrink-0 text-signal" weight="fill" />
              {lastStatus}
            </div>
          )}
        </div>

        <p className="mt-10 text-center font-mono text-[10px] text-muted">
          Autter Runtime — browser relay at /api/autter-runtime
        </p>
      </div>
    </main>
  );
}
